import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";

// Synchronous: most-hit landing page (instant first paint)
import LandingPage from "./pages/LandingPage";

// Lazy-loaded pages (code-split on demand → small initial bundle)
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const PropertiesPage = lazy(() => import("./pages/PropertiesPage"));
const PropertyDetailPage = lazy(() => import("./pages/PropertyDetailPage"));
const CalendarPageModern = lazy(() => import("./pages/CalendarPageModern"));
const MissionsPage = lazy(() => import("./pages/MissionsPage"));
const MissionDetailPage = lazy(() => import("./pages/MissionDetailPage"));
const ProvidersPage = lazy(() => import("./pages/ProvidersPage"));
const FinancialsPage = lazy(() => import("./pages/ComptabilitePage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const GuidePage = lazy(() => import("./pages/GuidePage"));
const ProviderDashboard = lazy(() => import("./pages/ProviderDashboard"));
const SubscriptionRequiredPage = lazy(() => import("./pages/SubscriptionRequiredPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const SetPasswordPage = lazy(() => import("./pages/SetPasswordPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogArticlePage = lazy(() => import("./pages/BlogArticlePage"));
const BlogArticleStubPage = lazy(() => import("./pages/BlogArticleStubPage"));
const ContractsPage = lazy(() => import("./pages/ContractsPage"));
const CheckinPage = lazy(() => import("./pages/CheckinPage"));
const GuestRequestsPage = lazy(() => import("./pages/GuestRequestsPage"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const GuestCheckinPage = lazy(() => import("./pages/GuestCheckinPage"));
const PilotagePage = lazy(() => import("./pages/PilotagePage"));
const AutoMessagesPage = lazy(() => import("./pages/AutoMessagesPage"));
const ReceiptsPage = lazy(() => import("./pages/ReceiptsPage"));
const DocumentsPage = lazy(() => import("./pages/DocumentsPage"));
const EtatDesLieuxPage = lazy(() => import("./pages/EtatDesLieuxPage"));
const EtatDesLieuxEditor = lazy(() => import("./pages/EtatDesLieuxEditor"));
const OzcbnbVsSmoobuPage = lazy(() => import("./pages/OzcbnbVsSmoobuPage"));
const OzcbnbVsLodgifyPage = lazy(() => import("./pages/OzcbnbVsLodgifyPage"));
const OzcbnbVsSuperhotePage = lazy(() => import("./pages/OzcbnbVsSuperhotePage"));
const OzcbnbVsHospitablePage = lazy(() => import("./pages/OzcbnbVsHospitablePage"));
const OzcbnbVsGuestyPage = lazy(() => import("./pages/OzcbnbVsGuestyPage"));
const OzcbnbVsHostawayPage = lazy(() => import("./pages/OzcbnbVsHostawayPage"));
const OzcbnbVsBookingsyncPage = lazy(() => import("./pages/OzcbnbVsBookingsyncPage"));
const OzcbnbVsIgmsPage = lazy(() => import("./pages/OzcbnbVsIgmsPage"));
const Beds24VsOzcbnbPage = lazy(() => import("./pages/Beds24VsOzcbnbPage"));
const TokeetVsOzcbnbPage = lazy(() => import("./pages/TokeetVsOzcbnbPage"));
const HostfullyVsOzcbnbPage = lazy(() => import("./pages/HostfullyVsOzcbnbPage"));
const AvantioVsOzcbnbPage = lazy(() => import("./pages/AvantioVsOzcbnbPage"));
const CloudbedsVsOzcbnbPage = lazy(() => import("./pages/CloudbedsVsOzcbnbPage"));
const ConciergerieAirbnbPage = lazy(() => import("./pages/ConciergerieAirbnbPage"));
const GuideHoteDebutantPage = lazy(() => import("./pages/GuideHoteDebutantPage"));
const SeoLandingPage = lazy(() => import("./pages/SeoLandingPage"));
const RessourcesPage = lazy(() => import("./pages/RessourcesPage"));
const CleaningCalendarPage = lazy(() => import("./pages/CleaningCalendarPage"));
const StockPage = lazy(() => import("./pages/StockPage"));
const MissionStockPage = lazy(() => import("./pages/MissionStockPage"));
const MethodeLeasePage = lazy(() => import("./pages/MethodeLeasePage"));
const CitySeoPage = lazy(() => import("./pages/CitySeoPage"));
const CitiesHubPage = lazy(() => import("./pages/CitySeoPage").then((m) => ({ default: m.CitiesHubPage })));
const OwnerPulsePage = lazy(() => import("./pages/OwnerPulsePage"));
const PublicOwnerReportPage = lazy(() => import("./pages/PublicOwnerReportPage"));

// Lightweight loading fallback (no big spinner = no layout shift)
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
    <div className="w-8 h-8 rounded-full border-2 border-white/20 border-t-white animate-spin" />
  </div>
);

// Protected Route Component
function ProtectedRoute({ children, requireSubscription = true }) {
  const { user, loading, isProvider, isSubscribed } = useAuth();

  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (isProvider) return <Navigate to="/provider" replace />;
  if (requireSubscription && !isSubscribed) {
    return <Navigate to="/subscription-required" replace />;
  }
  return React.cloneElement(children, { key: user?.id || 'owner' });
}

function ProviderRoute({ children }) {
  const { user, loading, isProvider } = useAuth();

  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isProvider) return <Navigate to="/dashboard" replace />;

  return React.cloneElement(children, { key: user?.id || 'provider' });
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/guide/:propertyId" element={<GuidePage />} />
        <Route path="/checkin/:propertyId/:reservationId" element={<GuestCheckinPage />} />
        <Route path="/cleaning/:token" element={<CleaningCalendarPage />} />
        <Route path="/rapport/:token" element={<PublicOwnerReportPage />} />
        <Route path="/payment-success" element={<PaymentSuccessPage />} />
        <Route path="/subscription-required" element={<SubscriptionRequiredPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/a-propos" element={<AboutPage />} />
        <Route path="/mentions-légales" element={<LegalPage />} />
        <Route path="/confidentialite" element={<PrivacyPage />} />
        <Route path="/fonctionnalites" element={<FeaturesPage />} />
        <Route path="/tarifs" element={<PricingPage />} />
        <Route path="/methode-lease" element={<MethodeLeasePage />} />
        <Route path="/logiciel-gestion-airbnb-france" element={<CitiesHubPage />} />
        <Route path="/logiciel-gestion-airbnb/:ville" element={<CitySeoPage />} />

        {/* SEO landing pages */}
        <Route path="/ressources" element={<RessourcesPage />} />
        <Route path="/channel-manager-airbnb" element={<SeoLandingPage />} />
        <Route path="/logiciel-gestion-airbnb" element={<SeoLandingPage />} />
        <Route path="/logiciel-conciergerie-airbnb" element={<SeoLandingPage />} />
        <Route path="/synchronisation-airbnb-booking" element={<SeoLandingPage />} />
        <Route path="/channel-manager-sans-commission" element={<SeoLandingPage />} />
        <Route path="/logiciel-airbnb-prix-fixe" element={<SeoLandingPage />} />
        <Route path="/comparatif/ozcbnb-vs-smoobu" element={<OzcbnbVsSmoobuPage />} />
        <Route path="/comparatif/ozcbnb-vs-lodgify" element={<OzcbnbVsLodgifyPage />} />
        <Route path="/comparatif/ozcbnb-vs-superhote" element={<OzcbnbVsSuperhotePage />} />
        <Route path="/comparatif/ozcbnb-vs-hospitable" element={<OzcbnbVsHospitablePage />} />
        <Route path="/comparatif/ozcbnb-vs-guesty" element={<OzcbnbVsGuestyPage />} />
        <Route path="/comparatif/ozcbnb-vs-hostaway" element={<OzcbnbVsHostawayPage />} />
        <Route path="/comparatif/ozcbnb-vs-bookingsync" element={<OzcbnbVsBookingsyncPage />} />
        <Route path="/comparatif/ozcbnb-vs-igms" element={<OzcbnbVsIgmsPage />} />
        <Route path="/comparatif/beds24-vs-ozcbnb" element={<Beds24VsOzcbnbPage />} />
        <Route path="/comparatif/tokeet-vs-ozcbnb" element={<TokeetVsOzcbnbPage />} />
        <Route path="/comparatif/hostfully-vs-ozcbnb" element={<HostfullyVsOzcbnbPage />} />
        <Route path="/comparatif/avantio-vs-ozcbnb" element={<AvantioVsOzcbnbPage />} />
        <Route path="/comparatif/cloudbeds-vs-ozcbnb" element={<CloudbedsVsOzcbnbPage />} />
        <Route path="/conciergerie-airbnb" element={<ConciergerieAirbnbPage />} />
        <Route path="/guide/hote-airbnb-debutant" element={<GuideHoteDebutantPage />} />
        <Route path="/comparatif" element={<ComparePage />} />
        <Route path="/blog" element={<BlogPage />} />

        {/* Explicit blog stubs (richer SEO content) — must come before catch-all /blog/:slug */}
        <Route path="/blog/comment-synchroniser-airbnb-booking-automatiquement" element={<BlogArticleStubPage />} />
        <Route path="/blog/eviter-double-reservation-airbnb-booking" element={<BlogArticleStubPage />} />
        <Route path="/blog/meilleure-alternative-smoobu-2026" element={<BlogArticleStubPage />} />
        <Route path="/blog/channel-manager-airbnb-guide-complet-2026" element={<BlogArticleStubPage />} />
        <Route path="/blog/logiciel-gestion-airbnb-gratuit-vs-payant" element={<BlogArticleStubPage />} />
        <Route path="/blog/:slug" element={<BlogArticlePage />} />

        {/* Protected Routes - Owner */}
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/properties" element={<ProtectedRoute><PropertiesPage /></ProtectedRoute>} />
        <Route path="/properties/:id" element={<ProtectedRoute><PropertyDetailPage /></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute><CalendarPageModern /></ProtectedRoute>} />
        <Route path="/missions" element={<ProtectedRoute><MissionsPage /></ProtectedRoute>} />
        <Route path="/missions/:id" element={<ProtectedRoute><MissionDetailPage /></ProtectedRoute>} />
        <Route path="/missions/:id/stock" element={<ProtectedRoute><MissionStockPage /></ProtectedRoute>} />
        <Route path="/stock" element={<ProtectedRoute><StockPage /></ProtectedRoute>} />
        <Route path="/providers" element={<ProtectedRoute><ProvidersPage /></ProtectedRoute>} />
        <Route path="/financials" element={<ProtectedRoute><FinancialsPage /></ProtectedRoute>} />
        <Route path="/owner-pulse" element={<ProtectedRoute><OwnerPulsePage /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
        <Route path="/contracts" element={<ProtectedRoute><ContractsPage /></ProtectedRoute>} />
        <Route path="/checkin" element={<ProtectedRoute><CheckinPage /></ProtectedRoute>} />
        <Route path="/demandes-voyageurs" element={<ProtectedRoute><GuestRequestsPage /></ProtectedRoute>} />
        <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
        <Route path="/pilotage" element={<ProtectedRoute><PilotagePage /></ProtectedRoute>} />
        <Route path="/auto-messages" element={<ProtectedRoute><AutoMessagesPage /></ProtectedRoute>} />
        <Route path="/quittances" element={<ProtectedRoute><ReceiptsPage /></ProtectedRoute>} />
        <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
        <Route path="/etat-des-lieux" element={<ProtectedRoute><EtatDesLieuxPage /></ProtectedRoute>} />
        <Route path="/etat-des-lieux/new" element={<ProtectedRoute><EtatDesLieuxEditor /></ProtectedRoute>} />
        <Route path="/etat-des-lieux/:id" element={<ProtectedRoute><EtatDesLieuxEditor /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute requireSubscription={false}><SettingsPage /></ProtectedRoute>} />

        {/* Provider Routes */}
        <Route path="/provider" element={<ProviderRoute><ProviderDashboard /></ProviderRoute>} />
        <Route path="/provider/missions/:id" element={<ProviderRoute><MissionDetailPage /></ProviderRoute>} />
        <Route path="/provider/missions/:id/stock" element={<ProviderRoute><MissionStockPage /></ProviderRoute>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange storageKey="ozc-theme">
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-center" />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
